import {
	PanZoomState,
	panZoomToStyle,
	usePanZoom,
	useToggle
} from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { IconButton } from "../IconButton/IconButton";
import styles from "./ImageViewer.module.less";
import classNames from "classnames";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";

export type ImageViewerProps = {
	imageUrl: string;
	isLoading?: boolean;
	isBackgroundToggleAvailable?: boolean;
	trackingId: string;
};

export const ImageViewer = (props: ImageViewerProps): ReactElement => {
	const {
		imageUrl,
		isLoading = false,
		isBackgroundToggleAvailable = false,
		trackingId
	} = props;

	const [panZoomState, setPanZoomState] = useState<PanZoomState>(DEFAULT_PAN_ZOOM);
	const [rotationDeg, setRotationDeg] = useState<number>(0);

	const zoomPanRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [isInversedBackground, toggleIsInversedBackground] = useToggle(false);

	const {
		zoom,
		hasPointersDown
	} = usePanZoom(
		setPanZoomState,
		zoomPanRef,
		{
			minScale: MIN_SCALE,
			maxScale: MAX_SCALE
		}
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(imageUrl);
			setPanZoomState(DEFAULT_PAN_ZOOM);
			setRotationDeg(0);
		},
		[imageUrl]
	);

	const resetHandler = useCallback(
		(): void => {
			setPanZoomState(DEFAULT_PAN_ZOOM);
			setRotationDeg(0);
		},
		[]
	);

	const rotateClockwiseHandler = useCallback(
		(): void => {
			setRotationDeg(
				(x): number => (x + 90) % 360
			);
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.content,
				{ [styles.content_inversed]: isInversedBackground && isBackgroundToggleAvailable }
			)}
		>
			{isLoading && (
				<BusyIndicator />
			)}

			{!isLoading && (
				<ImageViewerTools
					onZoom={zoom}
					onRotateClockwise={rotateClockwiseHandler}
					onReset={resetHandler}
					onToggleBackground={
						isBackgroundToggleAvailable
							? toggleIsInversedBackground
							: undefined
					}
					trackingId="tools"
					currentScale={panZoomState.scale}
				/>
			)}

			<div
				className={classNames(
					styles.zoomPanContainer,
					{ [styles.zoomPanContainer_panning]: hasPointersDown }
				)}
				ref={zoomPanRef}
			>
				{!isLoading && (
					<div
						className={styles.imageHost}
						style={panZoomToStyle(panZoomState)}
					>
						<img
							key={imageUrl}
							className={styles.image}
							src={imageUrl}
							draggable={false}
							style={{
								transform: `rotate(${rotationDeg}deg)`
							}}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
ImageViewer.displayName = "ImageViewer";

const DEFAULT_PAN_ZOOM: PanZoomState = {
	scale: 1,
	translateX: 0,
	translateY: 0
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;

type ImageViewerToolsProps = {
	onZoom: (action: "in" | "out") => void;
	onRotateClockwise: () => void;
	onReset: () => void;
	onToggleBackground?: () => void;
	currentScale: number;
	trackingId: string;
};

const ImageViewerTools = (props: ImageViewerToolsProps): ReactElement => {
	const {
		onZoom,
		onRotateClockwise,
		onReset,
		onToggleBackground,
		currentScale,
		trackingId
	} = props;

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	// Native listener since usePanZoom uses native listeners as well, and the react Synthetic
	// events come after the native events, so if you want to prevent bubbling to prevent a native
	// listener from being called, you need to use native events as well
	useEffect(
		(): (() => void) | void => {
			const { current: element } = ref;
			if (!element) {
				return;
			}

			const pointerDownHandler = (args: PointerEvent): void => {
				args.stopPropagation();
			};

			element.addEventListener("pointerdown", pointerDownHandler);

			return (): void => {
				element.removeEventListener("pointerdown", pointerDownHandler);
			};
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.tools}
			ref={ref}
		>
			<IconButton
				icon="plus"
				onClick={() => onZoom("in")}
				trackingId="in"
				kind="secondary"
				shape="round"
				isDisabled={currentScale >= MAX_SCALE}
				tooltip={localize("_ZoomIn")}
			/>

			<IconButton
				icon="minus"
				onClick={() => onZoom("out")}
				trackingId="out"
				kind="secondary"
				shape="round"
				isDisabled={currentScale <= MIN_SCALE}
				tooltip={localize("_ZoomOut")}
			/>

			<IconButton
				icon="arrow-rotate-right"
				onClick={() => onRotateClockwise()}
				trackingId="rotate-clockwise"
				kind="secondary"
				shape="round"
				tooltip={localize("_RotateClockwise")}
			/>

			<IconButton
				icon="expand"
				onClick={() => onReset()}
				trackingId="reset"
				kind="secondary"
				shape="round"
				tooltip={localize("_Reset")}
			/>

			{onToggleBackground && (
				<IconButton
					icon="circle-half-stroke"
					onClick={() => onToggleBackground()}
					trackingId="toggle-background"
					kind="secondary"
					shape="round"
					tooltip={localize("_ToggleBackground")}
				/>
			)}
		</div>
	);
};
ImageViewerTools.displayName = "ImageViewerTools";
